import {
  resolveLiveBookingPriceIls,
  sumCheckedSupplementsIls,
} from "./vpPriceContract";
import { resolveOurPriceDisplayIlsForAudit } from "./vpPriceResultsProfileAudit";
import { roomsAndPaxInfoHasManualAddedRooms } from "./vpTotalPaymentRoomSum";
import { isVpManualAddedRoom } from "./vpManualAddRoom";
import {
  resolveVacationPackageRoomMarkupIls,
  vacationPackageGlobalAgentMarkupIls,
} from "./vacationPackageRoomAgentMarkup";
import { resolveVpRoomCardDisplayPrice } from "./resolveVpRoomCardDisplayPrice";

const LS_PDF_SSOT = "DEBUG_VP_PDF_PRICE_SSOT";

/** @type {Record<string, string>} */
const _lastTraceFp = Object.create(null);

function _isDev() {
  try {
    return import.meta.env?.DEV === true;
  } catch {
    return false;
  }
}

function _verbose() {
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem(LS_PDF_SSOT) === "1";
  } catch {
    return false;
  }
}

function _num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function _round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

function _readDisallowedLegacyPrices(ourPrice, selectedResult) {
  const snap =
    ourPrice?.pricingSnapshot ??
    selectedResult?.pricingSnapshot ??
    selectedResult?.fullDeal?.pricingSnapshot ??
    null;
  const canon =
    ourPrice?.canonicalPricingSnapshotV1 ??
    selectedResult?.canonicalPricingSnapshotV1 ??
    null;

  return [
    {
      source: "pricingSnapshot.finalPriceIls",
      priceIls: _num(snap?.finalPriceIls ?? snap?.finalClientPriceIls),
    },
    {
      source: "pricingSnapshot.final_price",
      priceIls: _num(snap?.final_price),
    },
    {
      source: "canonicalPricingSnapshotV1.totals.finalPriceIls",
      priceIls: _num(canon?.totals?.finalPriceIls),
    },
    {
      source: "backendRoomComposePriceIls",
      priceIls: _num(selectedResult?.backendRoomComposePriceIls),
    },
    {
      source: "searchCardPriceIls",
      priceIls: _num(selectedResult?.searchCardPriceIls),
    },
  ];
}

/**
 * Profile/sidebar/sticky SSOT for VP quote PDF total (ILS, before display formatting).
 * @param {object} input
 */
export function resolveVpPdfTotalPrice(input = {}) {
  const {
    ourPrice = null,
    packageProfileDisplayOurPrice = null,
    paymentSummaryPriceIls = null,
    liveBookingPriceIls: liveBookingInput = null,
    billableAttractionsIls = 0,
    selectedResult = null,
    roomsAndPaxInfo = null,
    handleFeeData = null,
    ldsSupplement = null,
  } = input;

  const profileOurPrice = packageProfileDisplayOurPrice ?? ourPrice;
  const extraIls = _num(billableAttractionsIls) ?? 0;

  const legacyDisallowed = _readDisallowedLegacyPrices(ourPrice, selectedResult);
  const firstLegacy = legacyDisallowed.find((x) => x.priceIls != null && x.priceIls > 0);

  const oldPdfRaw =
    _num(ourPrice?.priceWithCreditCardFee) ??
    _num(ourPrice?.priceWithHandleFee) ??
    _num(ourPrice?.priceWithCommission) ??
    0;
  const oldPdfSource = "legacy:ourPrice.priceWithCreditCardFee|handleFee|commission";

  let selectedPdfPriceIls = null;
  let source = null;
  let usedFallback = false;

  const pwccCard = _num(profileOurPrice?.priceWithCreditCardFee);
  if (pwccCard != null && pwccCard > 0) {
    selectedPdfPriceIls = _round2(pwccCard + extraIls);
    source = "ourPrice.priceWithCreditCardFee";
  } else {
    const pwcc = _num(profileOurPrice?.priceWithCommissionAndCreditCardFee);
    if (pwcc != null && pwcc > 0) {
      selectedPdfPriceIls = _round2(pwcc + extraIls);
      source = "ourPrice.priceWithCommissionAndCreditCardFee";
    } else {
      const paySum = _num(paymentSummaryPriceIls);
      if (paySum != null && paySum > 0) {
        selectedPdfPriceIls = _round2(paySum + extraIls);
        source = "paymentSummaryPriceIls";
      } else {
        const liveExplicit = _num(liveBookingInput);
        const liveResolved =
          liveExplicit != null && liveExplicit > 0
            ? liveExplicit
            : resolveLiveBookingPriceIls(profileOurPrice);
        if (liveResolved != null && liveResolved > 0) {
          selectedPdfPriceIls = _round2(liveResolved + extraIls);
          source =
            liveExplicit != null && liveExplicit > 0
              ? "liveBookingPriceIls"
              : "liveBookingPriceIls(resolved)";
        } else {
          const audit = resolveOurPriceDisplayIlsForAudit(profileOurPrice);
          if (audit.value != null && audit.value > 0) {
            selectedPdfPriceIls = _round2(audit.value + extraIls);
            source = audit.path;
            usedFallback = true;
          } else if (firstLegacy?.priceIls != null) {
            selectedPdfPriceIls = _round2(firstLegacy.priceIls + extraIls);
            source = `fallback:${firstLegacy.source}`;
            usedFallback = true;
          }
        }
      }
    }
  }

  const sidebarBase = pwccCard ?? _num(profileOurPrice?.priceWithCreditCardFee);
  const sidebarPriceIls =
    sidebarBase != null && sidebarBase > 0 ? _round2(sidebarBase + extraIls) : null;
  const stickyPriceIls = sidebarPriceIls;

  const roomsCount = Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo.length : 0;
  const hasManualRooms = roomsAndPaxInfoHasManualAddedRooms(roomsAndPaxInfo);
  let hasRoomMarkup = false;
  if (Array.isArray(roomsAndPaxInfo)) {
    for (const slot of roomsAndPaxInfo) {
      const m = resolveVacationPackageRoomMarkupIls(slot, handleFeeData);
      if (m != null && m > 0) {
        hasRoomMarkup = true;
        break;
      }
    }
  }
  const globalMarkup = vacationPackageGlobalAgentMarkupIls(handleFeeData);

  return {
    selectedPdfPriceIls,
    source,
    sidebarPriceIls,
    stickyPriceIls,
    billableAttractionsIls: extraIls,
    oldPdfPriceIls: _round2(oldPdfRaw + extraIls),
    oldPdfSource,
    usedFallback,
    roomsCount,
    hasManualRooms,
    hasRoomMarkup,
    handleFee: _num(profileOurPrice?.handleFee) ?? globalMarkup ?? null,
    selectedSupplements: sumCheckedSupplementsIls(ldsSupplement),
    ourPriceKeys: profileOurPrice ? Object.keys(profileOurPrice).slice(0, 24) : [],
    legacyCandidatesSkipped: legacyDisallowed
      .filter((x) => x.priceIls != null)
      .map((x) => ({ source: x.source, priceIls: x.priceIls })),
  };
}

/** ILS display for VP PDF — never multiplies by catalogue currentRate (unlike convertPrice). */
export function formatVpPdfPriceIls(amountIls) {
  const n = Number(amountIls);
  if (!Number.isFinite(n)) return "0₪";
  return `${Math.round(n).toLocaleString("en-US")}₪`;
}

/**
 * @param {number|null|undefined} amountIls
 * @param {(n: number) => number} [ilsToUsd]
 */
export function formatVpPdfPriceWithUsdParenthetical(amountIls, ilsToUsd) {
  const ilsStr = formatVpPdfPriceIls(amountIls);
  if (typeof ilsToUsd !== "function") return ilsStr;
  const usd = Number(ilsToUsd(Number(amountIls)));
  if (!Number.isFinite(usd) || usd <= 0) return ilsStr;
  const usdRounded = Math.ceil(usd * 100) / 100;
  return `${ilsStr} (${usdRounded}$)`;
}

/**
 * Per-room lines for VP PDF breakdown (display-only).
 */
export function buildVpPdfRoomsPricingLines({
  roomsAndPaxInfo = [],
  ourPrice = null,
  packageProfileDisplayOurPrice = null,
  handleFeeData = null,
  ilsToUsd,
  billableAttractionsIls = 0,
}) {
  const slots = Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo : [];
  const profilePrice = packageProfileDisplayOurPrice ?? ourPrice;

  return slots.map((slot, roomIndex) => {
    const room = slot?.room ?? {};
    const roomName = room?.roomType || room?.roomSet || `חדר ${roomIndex + 1}`;
    const resolved = resolveVpRoomCardDisplayPrice({
      room,
      roomIndex,
      roomsAndPaxInfo: slots,
      ourPrice: profilePrice,
      handleFeeData,
      billableAttractionsIls,
      ilsToUsd,
    });
    const markupIls = resolveVacationPackageRoomMarkupIls(slot, handleFeeData);
    const isManual = isVpManualAddedRoom(room, slot);

    return {
      roomName,
      displayPriceIls: resolved.displayPriceIls,
      priceSource: resolved.source,
      roomMarkupIls: markupIls > 0 ? markupIls : null,
      isManualAddedRoom: isManual,
    };
  });
}

/**
 * @param {ReturnType<typeof resolveVpPdfTotalPrice> & { dealId?: string|null }} payload
 * @param {{ force?: boolean }} [opts]
 */
export function logVpPdfPriceSsotTrace(payload, opts = {}) {
  if (!_isDev() || !payload) return;
  const key = String(payload.dealId ?? payload.source ?? "vp-pdf");
  const fp = JSON.stringify(payload);
  if (!opts.force && !_verbose() && _lastTraceFp[key] === fp) return;
  _lastTraceFp[key] = fp;
  // eslint-disable-next-line no-console
  console.info("[VP_PDF_PRICE_SSOT_TRACE]", payload);
}
